/**
 * Les factures d'un sous-traitant, et ce qu'il reste à lui payer.
 *
 * Décidé avec le patron le 30 octobre 2026, quatrième volet du phasage. La
 * base stocke ce qui est écrit sur la facture ; tout le reste se calcule ici,
 * en un seul endroit :
 *
 *   · **la TVA** — par défaut **autoliquidée** (CGI, art. 283-2 nonies) : le
 *     sous-traitant du BTP facture hors taxes, on lui règle le HT, et c'est
 *     l'entreprise qui déclare la TVA, qu'elle déduit dans la foulée. Un
 *     sous-traitant en franchise n'en facture pas ; une prestation qui n'est
 *     pas des travaux en porte une normale, qu'on lui paie ;
 *   · **la retenue de garantie** (loi du 16 juillet 1971) — au plus 5 % de ce
 *     qu'on lui doit, gardés jusqu'à un an après la réception ;
 *   · **le reste à payer** — le dû net de retenue, moins les règlements.
 *
 * Tout ici est pur.
 */
import { round2 } from "../../money.js";
import type { SubcontractPaidBy } from "./approval.js";
import type { ProjectSubcontract } from "./types.js";

export type SubcontractVatMode = "autoliquidation" | "normale" | "franchise";

export const SUBCONTRACT_VAT_MODE_LABELS: Record<SubcontractVatMode, string> = {
  autoliquidation: "Autoliquidation",
  normale: "TVA facturée",
  franchise: "Franchise en base",
};

export const SUBCONTRACT_VAT_RATES = [20, 10, 5.5, 2.1] as const;

/** Le plafond légal de la retenue de garantie. */
export const MAX_RETENTION_PERCENT = 5;

export interface SubcontractInvoice {
  id: string;
  projectId: string;
  subcontractId: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string | null;
  amountHt: number;
  vatMode: SubcontractVatMode;
  vatRate: number;
  retentionPercent: number;
  notes: string | null;
}

export type SubcontractPaymentKind = "invoice" | "retention";

export interface SubcontractPayment {
  id: string;
  projectId: string;
  subcontractId: string;
  kind: SubcontractPaymentKind;
  /** La facture réglée ; nul pour la retenue rendue. */
  invoiceId: string | null;
  amount: number;
  paidOn: string;
  /** Par l'entreprise, ou par le client sous délégation ou paiement direct. */
  paidBy: SubcontractPaidBy;
  notes: string | null;
}

export interface InvoiceAmounts {
  ht: number;
  /** La TVA des travaux, qu'on la paie ou qu'on l'autoliquide. */
  vat: number;
  /** Celle qu'on autoliquide, à porter sur la déclaration. Zéro sinon. */
  selfAssessedVat: number;
  /** Ce que la facture réclame : HT, plus la TVA quand le sous-traitant la facture. */
  gross: number;
  /** La retenue de garantie, sur ce montant. */
  retention: number;
  /** Ce qu'on lui doit maintenant. */
  net: number;
}

type InvoiceRef = Pick<SubcontractInvoice, "amountHt" | "vatMode" | "vatRate" | "retentionPercent">;

/**
 * Les montants d'une facture. La retenue porte sur ce qui est réellement
 * dû au sous-traitant — en autoliquidation, le HT : la TVA ne lui est pas
 * payée, on ne peut pas la lui retenir.
 */
export function invoiceAmounts(inv: InvoiceRef): InvoiceAmounts {
  const ht = round2(inv.amountHt);
  const vat = inv.vatMode === "franchise" ? 0 : round2((ht * inv.vatRate) / 100);
  const gross = inv.vatMode === "normale" ? round2(ht + vat) : ht;
  const retention = round2((gross * inv.retentionPercent) / 100);
  return {
    ht,
    vat,
    selfAssessedVat: inv.vatMode === "autoliquidation" ? vat : 0,
    gross,
    retention,
    net: round2(gross - retention),
  };
}

export type InvoicePaymentStatus = "paid" | "partial" | "unpaid" | "overdue";

export const INVOICE_PAYMENT_STATUS_LABELS: Record<InvoicePaymentStatus, string> = {
  paid: "Réglée",
  partial: "Réglée en partie",
  unpaid: "À régler",
  overdue: "En retard",
};

export interface SubcontractInvoiceBalance extends InvoiceAmounts {
  paid: number;
  remaining: number;
  status: InvoicePaymentStatus;
}

/** Ce qui est réglé d'une facture, et ce qu'il en reste. */
export function invoiceBalance(
  inv: InvoiceRef & Pick<SubcontractInvoice, "id" | "dueDate">,
  payments: readonly Pick<SubcontractPayment, "kind" | "invoiceId" | "amount">[],
  today: string,
): SubcontractInvoiceBalance {
  const amounts = invoiceAmounts(inv);
  const paid = round2(
    payments.filter((p) => p.kind === "invoice" && p.invoiceId === inv.id).reduce((s, p) => s + p.amount, 0),
  );
  const remaining = round2(Math.max(0, amounts.net - paid));
  const status: InvoicePaymentStatus =
    remaining === 0
      ? "paid"
      : inv.dueDate != null && inv.dueDate < today
        ? "overdue"
        : paid > 0
          ? "partial"
          : "unpaid";
  return { ...amounts, paid, remaining, status };
}

export interface SubcontractAccount {
  /** Le montant du marché. */
  contractHt: number;
  invoicedHt: number;
  /** Ce que le sous-traitant n'a pas encore facturé du marché. */
  toInvoiceHt: number;
  /** Ce qu'il a facturé au-delà : un avenant à signer, ou une erreur. */
  overInvoicedHt: number;
  /** Ce qui lui est dû, net de retenue, sur toutes ses factures. */
  netDue: number;
  paid: number;
  /** Dans ce qui est réglé, ce que le client a payé lui-même. */
  paidByClient: number;
  /** Le reste à payer, retenue non comprise. */
  remaining: number;
  /** Ce qui est en retard dans ce reste. */
  overdue: number;
  /** La retenue gardée sur toutes les factures. */
  retained: number;
  retentionReleased: number;
  /** La retenue à lui rendre, un an après la réception. */
  retentionHeld: number;
  selfAssessedVat: number;
}

/** Le compte d'un marché : facturé, réglé, reste à payer, retenue. */
export function subcontractAccount(
  contract: Pick<ProjectSubcontract, "id" | "amountHt">,
  invoices: readonly SubcontractInvoice[],
  payments: readonly SubcontractPayment[],
  today: string,
): SubcontractAccount {
  const siennes = invoices.filter((i) => i.subcontractId === contract.id);
  const reglements = payments.filter((p) => p.subcontractId === contract.id);
  let invoiced = 0;
  let netDue = 0;
  let paid = 0;
  let remaining = 0;
  let overdue = 0;
  let retained = 0;
  let vat = 0;
  for (const inv of siennes) {
    const b = invoiceBalance(inv, reglements, today);
    invoiced += b.ht;
    netDue += b.net;
    paid += b.paid;
    remaining += b.remaining;
    if (b.status === "overdue") overdue += b.remaining;
    retained += b.retention;
    vat += b.selfAssessedVat;
  }
  const parLeClient = reglements.filter((p) => p.paidBy === "client").reduce((s, p) => s + p.amount, 0);
  const released = reglements.filter((p) => p.kind === "retention").reduce((s, p) => s + p.amount, 0);
  return {
    contractHt: round2(contract.amountHt),
    invoicedHt: round2(invoiced),
    toInvoiceHt: round2(Math.max(0, contract.amountHt - invoiced)),
    overInvoicedHt: round2(Math.max(0, invoiced - contract.amountHt)),
    netDue: round2(netDue),
    paid: round2(paid),
    paidByClient: round2(parLeClient),
    remaining: round2(remaining),
    overdue: round2(overdue),
    retained: round2(retained),
    retentionReleased: round2(released),
    retentionHeld: round2(Math.max(0, retained - released)),
    selfAssessedVat: round2(vat),
  };
}

/** Le HT facturé par marché : ce que la marge compare à l'avancement. */
export function invoicedHtByContract(
  invoices: readonly Pick<SubcontractInvoice, "subcontractId" | "amountHt">[],
): Map<string, number> {
  const m = new Map<string, number>();
  for (const i of invoices) m.set(i.subcontractId, round2((m.get(i.subcontractId) ?? 0) + i.amountHt));
  return m;
}
